import React, { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { toast } from "sonner";
import { Users, Check, ArrowRight } from "lucide-react";
import { WEB3FORMS_ACCESS_KEY } from "../config";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.section>
  );
};

const JoinPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    designation: '',
    organization: '',
    city: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          subject: "New Community Member Request - GSLC",
          from_name: "GSLC Community",
          ...formData
        }),
      });
      
      const result = await response.json();
      if (result.success) {
        toast.success("Welcome to GSLC! We'll be in touch soon.");
        setFormData({ name: '', email: '', phone: '', designation: '', organization: '', city: '', message: '' });
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    "Access to premium leadership forums",
    "Networking with like-minded decision makers",
    "Early access to GSLC events & initiatives",
    "Opportunities to speak, collaborate, and showcase innovations",
    "Exclusive educational resources and insights",
    "Connect with 14,000+ school leaders across India"
  ];

  return (
    <div className="pt-24">
      {/* Hero */}
      <AnimatedSection className="section-padding bg-gradient-to-br from-yellow-50 via-white to-red-50">
        <div className="container-custom">
          <motion.div variants={fadeInUp} className="max-w-4xl mx-auto text-center">
            <span className="font-accent text-red-600 text-sm tracking-[0.2em] uppercase">Be Part of Something Big</span>
            <h1 className="font-heading text-4xl md:text-6xl font-semibold mt-4 mb-6 text-slate-800">
              Join Our <span className="gradient-text">Community</span>
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Connect with India's leading school leaders and transform education together. 
              Be part of a movement that's shaping the future of school leadership.
            </p>
          </motion.div>
        </div>
      </AnimatedSection>

      {/* Form & Benefits */}
      <AnimatedSection className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Benefits */}
            <motion.div variants={fadeInUp}>
              <div className="card-light rounded-2xl p-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-yellow-400 via-red-500 to-blue-600 flex items-center justify-center mb-6">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <h2 className="font-heading text-3xl text-slate-800 mb-6">What Our Community Gets</h2>
                
                <ul className="space-y-4">
                  {benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-yellow-400 to-red-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-slate-600">{benefit}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-8 p-6 rounded-xl bg-gradient-to-br from-blue-50 to-yellow-50 border border-blue-100">
                  <p className="font-heading text-xl text-blue-800 mb-2">14,000+ School Leaders</p>
                  <p className="text-slate-600 text-sm">Already part of the GSLC community across 50+ cities in India</p>
                </div>
              </div>
            </motion.div>

            {/* Join Form */}
            <motion.div variants={fadeInUp}>
              <form onSubmit={handleSubmit} className="card-light rounded-2xl p-8 space-y-6" data-testid="join-form">
                <h3 className="font-heading text-2xl text-slate-800 mb-2">Join GSLC Community</h3>
                <p className="text-slate-500 text-sm mb-6">Fill out the form below to become a member.</p>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm text-slate-600 mb-2 font-medium">Full Name *</label>
                    <input
                      type="text"
                      required
                      data-testid="join-name-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full bg-slate-50 border-2 border-slate-200 rounded-xl px-4 py-3 text-slate-800 placeholder:text-slate-400 focus:border-red-500 focus:bg-white focus:outline-none transition-all"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-600 mb-2 font-medium">Email *</label>
                    <input
                      type="email"
                      required
                      data-testid="join-email-input"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full bg-slate-50 border-2 border-slate-200 rounded-xl px-4 py-3 text-slate-800 placeholder:text-slate-400 focus:border-red-500 focus:bg-white focus:outline-none transition-all"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm text-slate-600 mb-2 font-medium">Phone *</label>
                    <input
                      type="tel"
                      required
                      data-testid="join-phone-input"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full bg-slate-50 border-2 border-slate-200 rounded-xl px-4 py-3 text-slate-800 placeholder:text-slate-400 focus:border-red-500 focus:bg-white focus:outline-none transition-all"
                      placeholder="+91 XXXXX XXXXX"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-600 mb-2 font-medium">Designation *</label>
                    <input
                      type="text"
                      required
                      data-testid="join-designation-input"
                      value={formData.designation}
                      onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                      className="w-full bg-slate-50 border-2 border-slate-200 rounded-xl px-4 py-3 text-slate-800 placeholder:text-slate-400 focus:border-red-500 focus:bg-white focus:outline-none transition-all"
                      placeholder="Principal, Director, etc."
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm text-slate-600 mb-2 font-medium">Organization *</label>
                    <input
                      type="text"
                      required
                      data-testid="join-organization-input"
                      value={formData.organization}
                      onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                      className="w-full bg-slate-50 border-2 border-slate-200 rounded-xl px-4 py-3 text-slate-800 placeholder:text-slate-400 focus:border-red-500 focus:bg-white focus:outline-none transition-all"
                      placeholder="School/Institution name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-600 mb-2 font-medium">City *</label>
                    <input
                      type="text"
                      required
                      data-testid="join-city-input"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      className="w-full bg-slate-50 border-2 border-slate-200 rounded-xl px-4 py-3 text-slate-800 placeholder:text-slate-400 focus:border-red-500 focus:bg-white focus:outline-none transition-all"
                      placeholder="Your city"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-slate-600 mb-2 font-medium">Message (Optional)</label>
                  <textarea
                    data-testid="join-message-input"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={4}
                    className="w-full bg-slate-50 border-2 border-slate-200 rounded-xl px-4 py-3 text-slate-800 placeholder:text-slate-400 focus:border-red-500 focus:bg-white focus:outline-none transition-all resize-none"
                    placeholder="Tell us about yourself..."
                  />
                </div>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="join-submit-btn"
                  className="w-full btn-gradient disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? "Submitting..." : (
                    <>
                      Join GSLC Community <ArrowRight size={18} />
                    </>
                  )}
                </motion.button>
              </form>
            </motion.div>
          </div>
        </div>
      </AnimatedSection>

      {/* Footer CTA */}
      <AnimatedSection className="section-padding bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <div className="container-custom">
          <motion.div variants={fadeInUp} className="text-center">
            <h2 className="font-heading text-3xl md:text-4xl text-white mb-4">
              One Network. Many Voices. <span className="text-yellow-400">One Future for Education.</span>
            </h2>
            <p className="text-white/80 max-w-2xl mx-auto">
              GSLC – Connecting Leaders. Inspiring Change. Transforming Education.
            </p>
          </motion.div>
        </div>
      </AnimatedSection>
    </div>
  );
};

export default JoinPage;
